import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { bibleApi } from "./bible-api";
import {
  BibleVersion,
  BibleBook,
  BibleChapter,
  BibleVerse,
  Language,
  BibleVersionNew,
} from "./types";
import ImportBibleVersionModal from "./components/ImportBibleVersionModal";
import VerseComparison from "./components/VerseComparison";

export default function BibleManagement() {
  const [versions, setVersions] = useState<BibleVersion[]>([]);
  const [books, setBooks] = useState<BibleBook[]>([]);
  const [chapters, setChapters] = useState<BibleChapter[]>([]);
  const [verses, setVerses] = useState<BibleVerse[]>([]);
  const [sideBySideData, setSideBySideData] = useState<{[id:number]:BibleVersionNew}>({});
  const [languages, setLanguages] = useState<Language[]>([]);

  const [selectedLanguage, setSelectedLanguage] = useState<string>("");
  const [selectedVersions, setSelectedVersions] = useState<number[]>([]);
  const [selectedBook, setSelectedBook] = useState<number | null>(null);
  const [selectedBookData, setSelectedBookData] = useState<BibleBook | null>(null);
  const [selectedChapter, setSelectedChapter] = useState<number | null>(null);
  const [searchText, setSearchText] = useState<string>("");
  const [viewMode, setViewMode] = useState<"view" | "compare">("view");

  const [loading, setLoading] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);

  useEffect(() => {
    fetchInitialData();
  }, []);

//   useEffect(() => {
//     if (selectedBook) {
//       fetchChapters(selectedBook);
//     } else {
//       setChapters([]);
//       setVerses([]);
//     }
//   }, [selectedBook]);

  useEffect(() => {
    if (selectedBook && selectedChapter) {
      fetchVerses();
    } else {
      setVerses([]);
      setSideBySideData({});
    }
  }, [selectedBook, selectedChapter, selectedVersions, viewMode]);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const [versionsData, booksData/*, languagesData*/] = await Promise.all([
        bibleApi.getAllVersions(),
        bibleApi.getAllBooks(),
        // bibleApi.getLanguages(),
      ]);
      setVersions(versionsData);
      setBooks(booksData||[]);
      // setLanguages(languagesData);
    } catch (error: any) {
      setBooks( []);
        toast.error("Failed to load Bible data");
      
    } finally {
      setLoading(false);
    }
  };

  const fetchChapters = async (bookId: number) => {
    try {
      const chaptersData = await bibleApi.getChaptersByBook(bookId);
      setChapters(chaptersData);
      setSelectedChapter(null);
      setVerses([]);
    } catch (error: any) {
      toast.error("Failed to load chapters");
    }
  };

  const fetchVerses = async () => {
    if (!selectedBook || !selectedChapter) return;

    setLoading(true);
    try {
      if (viewMode === "compare" && selectedVersions.length > 0) {
        const comparisonVerses = await bibleApi.getVersesForComparison(
          selectedVersions,
          selectedBook,
          selectedChapter
        );
        setVerses(comparisonVerses);
        setSideBySideData({});
      } else if (selectedVersions.length > 0) {
        const versesData = await bibleApi.getVerses({
          versionIds: selectedVersions,
          bookId: selectedBook,
          chapterNumber: selectedChapter,
        });
        setSideBySideData(versesData);
        setVerses([]);
      }
    } catch (error: any) {
      toast.error("Failed to load verses");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchText.trim()) {
      toast.warning("Please enter search text");
      return;
    }

    setLoading(true);
    try {
      const searchParams = {
        searchText,
        language: selectedLanguage || undefined,
        versionIds: selectedVersions.length > 0 ? selectedVersions : undefined,
        bookId: selectedBook || undefined,
        chapterNumber: selectedChapter || undefined,
      };

      const searchResults = await bibleApi.searchVerses(searchParams);
      setVerses(searchResults);
      if (searchResults.length === 0) {
        toast.info("No verses found matching your search");
      }
    } catch (error: any) {
      toast.error("Search failed");
    } finally {
      setLoading(false);
    }
  };

  const handleVersionToggle = (versionId: number) => {
    setSelectedVersions((prev) => {
      if (prev.includes(versionId)) {
        return prev.filter((id) => id !== versionId);
      } else {
        return [...prev, versionId];
      }
    });
  };

  const handleClearFilters = () => {
    setSelectedLanguage("");
    setSelectedVersions([]);
    setSelectedBook(null);
    setSelectedChapter(null);
    setSearchText("");
    setVerses([]);
    setSideBySideData({});
  };

  const handleImportSuccess = () => {
    fetchInitialData();
  };

  const filteredVersions = selectedLanguage
    ? versions.filter((v) => v.languageCode === selectedLanguage)
    : versions;

  const selectedBookName = books.find((b) => b.index === selectedBook)?.name || "";

  const comparisonData = verses.map((verse) => {
    const version = versions.find((v) => v.id === verse.verse_index);
    return {
      version: version!,
      verse,
    };
  });

  // Process side-by-side data for multi-column view
  const getMultiColumnVerses = () => {
    if (Object.keys(sideBySideData).length === 0 || !selectedChapter) return null;

    const versionIds = selectedVersions.filter(id => sideBySideData[id]);
    if (versionIds.length === 0) return null;

    // Get all verse indices from all versions
    const allVerseIndices = new Set<number>();
    versionIds.forEach(versionId => {
      const versionData = sideBySideData[versionId];
      const chapterVerses = versionData.chapters[selectedChapter] || [];
      chapterVerses.forEach(verse => {
        allVerseIndices.add(verse.verse_index);
      });
    });

    // Sort verse indices
    const sortedVerseIndices = Array.from(allVerseIndices).sort((a, b) => a - b);

    // Create grid data structure: rows (verses) x columns (versions)
    return {
      versionIds,
      verseIndices: sortedVerseIndices,
      getVerse: (versionId: number, verseIndex: number) => {
        const versionData = sideBySideData[versionId];
        if (!versionData) return null;
        const chapterVerses = versionData.chapters[selectedChapter] || [];
        return chapterVerses.find(v => v.verse_index === verseIndex) || null;
      }
    };
  };

  const multiColumnData = getMultiColumnVerses();

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 py-8 px-4">
      <div className="max-w-[1600px] mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                Bible Management
              </h1>
              <p className="text-gray-600 dark:text-gray-300">
                View, search, and compare Bible versions
              </p>
            </div>
            <button
              onClick={() => setImportModalOpen(true)}
              className="btn-primary px-4 py-2 flex items-center space-x-2"
            >
              <i className="fa-solid fa-upload"></i>
              <span>Import Bible Version</span>
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            {/* Language Filter */}
            {/* <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Bible Version
              </label>
              <select
                value={selectedLanguage}
                onChange={(e) => setSelectedLanguage(e.target.value)}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-black dark:text-white"
              >
                <option value="">All Bible Versions</option>
                {languages.map((lang) => (
                  <option key={lang.code} value={lang.code}>
                    {lang.name}
                  </option>
                ))}
              </select>
            </div> */}

            {/* Book Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Book
              </label>
              <select
                value={selectedBook || ""}
                onChange={(e) => {
                  const bookIndex = e.target.value ? Number(e.target.value) : null;
                  setSelectedBook(bookIndex);
                  
                  if (bookIndex !== null) {
                    const book = books.find((b) => b.index === bookIndex);
                    setSelectedBookData(book || null);
                  } else {
                    setSelectedBookData(null);
                  }
                }}
                disabled={loading || books.length === 0}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-black dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="">Select Book</option>
                {books.map((book) => (
                  <option key={book.id} value={book.index}>
                    {book.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Chapter Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Chapter
              </label>
              <select
                value={selectedChapter || ""}
                onChange={(e) => setSelectedChapter(e.target.value ? Number(e.target.value) : null)}
                disabled={loading || !selectedBook || !selectedBookData || !selectedBookData.chapters || selectedBookData.chapters.length === 0}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-black dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="">Select Chapter</option>
                {selectedBookData?.chapters.map((chapter) => (
                  <option key={chapter.chapter_index} value={chapter.chapter_index}>
                      {chapter.chapter_index}
                  </option>
                ))}
              </select>
            </div>

            {/* View Mode */}
            {/* <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                View Mode
              </label>
              <select
                value={viewMode}
                onChange={(e) => setViewMode(e.target.value as "view" | "compare")}
                className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-black dark:text-white"
              >
                <option value="view">Single View</option>
                <option value="compare">Compare</option>
              </select>
            </div> */}
          </div>

          {/* Version Selection */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Bible Version(s) {viewMode === "compare" && "(Select multiple)"}
            </label>
            <div className="flex flex-wrap gap-2">
              {filteredVersions.map((version) => (
                <label
                  key={version.id}
                  className={`flex items-center px-3 py-2 rounded-lg border cursor-pointer transition-colors ${selectedVersions.includes(version.id)
                    ? "bg-blue-100 dark:bg-blue-900/30 border-blue-500 text-blue-700 dark:text-blue-300"
                    : "bg-gray-50 dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600"
                    }`}
                >
                  <input
                    type={viewMode === "compare" ? "checkbox" : "checkbox"}
                    checked={selectedVersions.includes(version.id)}
                    onChange={() => {
                      if (viewMode === "compare") {
                        handleVersionToggle(version.id);
                      } else {
                        handleVersionToggle(version.id);

                        // setSelectedVersions([version.id]);
                      }
                    }}
                    className="mr-2"
                  />
                  <span className="text-sm">
                    {version.name} {version.abbreviation && `(${version.abbreviation})`}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Search */}
          <div className="flex gap-2 mb-4">
            <input
              type="text"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && handleSearch()}
              placeholder="Search verses by text..."
              className="flex-1 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-black dark:text-white"
            />
            <button
              onClick={handleSearch}
              className="btn-primary px-6 py-2"
            >
              <i className="fa-solid fa-search mr-2"></i>
              Search
            </button>
            <button
              onClick={handleClearFilters}
              className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              <i className="fa-solid fa-times mr-2"></i>
              Clear
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          ) : viewMode === "compare" && verses.length > 0 ? (
            <VerseComparison
              verses={comparisonData}
              bookName={selectedBookName}
              chapterNumber={selectedChapter || 0}
            />
          ) : multiColumnData ? (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                {selectedBookName} {selectedChapter}
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b-2 border-gray-300 dark:border-gray-600">
                      <th className="sticky left-0 bg-white dark:bg-gray-800 px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-300 border-r-2 border-gray-300 dark:border-gray-600 z-10 min-w-[80px]">
                        Verse
                      </th>
                      {multiColumnData.versionIds.map((versionId) => {
                        const versionData = sideBySideData[versionId];
                        const version = versions.find(v => v.id === versionId);
                        return (
                          <th
                            key={versionId}
                            className="px-4 py-3 text-left font-semibold text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-700/50 min-w-[300px]"
                          >
                            <div>
                              <div className="font-semibold">
                                {version?.name || versionData?.version_name || `Version ${versionId}`}
                                {version?.abbreviation && (
                                  <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">
                                    ({version.abbreviation})
                                  </span>
                                )}
                              </div>
                              {versionData && (
                                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                  {versionData.bible_name}
                                </div>
                              )}
                            </div>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {multiColumnData.verseIndices.map((verseIndex) => (
                      <tr
                        key={verseIndex}
                        className="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/30"
                      >
                        <td className="sticky left-0 bg-white dark:bg-gray-800 px-4 py-3 font-semibold text-blue-600 dark:text-blue-400 border-r-2 border-gray-300 dark:border-gray-600 z-10">
                          {verseIndex}
                        </td>
                        {multiColumnData.versionIds.map((versionId) => {
                          const verse = multiColumnData.getVerse(versionId, verseIndex);
                          return (
                            <td
                              key={versionId}
                              className="px-4 py-3 text-gray-800 dark:text-gray-200 align-top"
                            >
                              {verse ? (
                                <div>
                                  <p className="leading-relaxed">{verse.text}</p>
                                  {verse.reference_verses_csv && verse.reference_verses_csv !== verseIndex.toString() && (
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                      Ref: {verse.reference_verses_csv}
                                    </p>
                                  )}
                                </div>
                              ) : (
                                <span className="text-gray-400 dark:text-gray-500 italic text-sm">
                                  —
                                </span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              {selectedBook && selectedChapter
                ? "Select a Bible version to view verses"
                : "Select a book and chapter, or search for verses"}
            </div>
          )}
        </div>
      </div>

      <ImportBibleVersionModal
        open={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        onSuccess={handleImportSuccess}
      />
    </div>
  );
}

